import { AsyncPipe, CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TuiDay } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiError,
  TuiLink,
  TuiLoader,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, of } from "rxjs";
import { LeaveService } from "../../../core/services/leave.service";
import { NurseService } from "../../../core/services/nurse.service";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import {
  EMPLOYMENT_TYPE,
  getEmployeeNumber,
  LEAVE_TYPE,
} from "../../../model/interface/nurse";

@Component({
  selector: "app-add-leave",
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiFade,
    TuiLink,
    TuiLoader,
    TuiNavigation,
    TuiTextfield,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiComboBoxModule,
    TuiSelectModule,
    TuiDataListWrapper,
    RouterLink,
    TuiInputDateModule,
    TuiStringifyContentPipe,
  ],
  templateUrl: "./add-leave.component.html",
  styleUrl: "./add-leave.component.scss",
  providers: [DatePipe],
})
export class AddLeaveComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital" },
    { label: "Nurses", link: "admin/nurse" },
    { label: "Leaves", link: "admin/leave" },
    { label: "Add New Leave" },
  ];

  leaveForm: FormGroup;
  isLoading = false;
  leaveId: string | null = null;
  nurses: any[] = [];
  leaveTypeOptions: { value: string; label: string }[] = [];
  balance: any = null;
  preview: any = null;
  expandedDates: string[] = [];
  pendingConflicts: any[] = [];
  showConflictDialog = false;
  employeeNumberDisplay = "—";
  joinDateDisplay = "—";

  stringifyNurse = (n: any) => (n ? n.name : "");
  stringifyLeaveType = (t: any) => (t?.label ? t.label : t || "");

  search = "";

  get filteredNurses(): any[] {
    const searchVal = (this.search || "").trim().toLowerCase();
    if (!searchVal) {
      return this.nurses;
    }
    return this.nurses.filter((nurse) =>
      this.stringifyNurse(nurse).toLowerCase().includes(searchVal)
    );
  }

  get availableCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.entitled ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.entitled ?? 0
      : 0;
    return day + annual;
  }

  get usedCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.used ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.used ?? 0
      : 0;
    return day + annual;
  }

  get remainingCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.remaining ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.remaining ?? 0
      : 0;
    return day + annual;
  }

  private alerts = inject(TuiAlertService);

  constructor(
    private fb: FormBuilder,
    private leaveService: LeaveService,
    private nurseService: NurseService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.leaveForm = this.fb.group({
      nurse: [null, Validators.required],
      submittedDate: [
        TuiDay.fromLocalNativeDate(new Date()),
        Validators.required,
      ],
      leaveType: [null, Validators.required],
      fromDate: [null, Validators.required],
      toDate: [null],
      reason: [""],
    }, { validators: this.dateRangeValidator() });
  }

  ngOnInit(): void {
    this.loadNurses();
    this.leaveId = this.route.snapshot.paramMap.get("id");
    if (this.leaveId) {
      this.breadcrumbs[3] = { label: "Edit Leave" };
      this.loadLeave(this.leaveId);
    }

    this.leaveForm.get("nurse")?.valueChanges.subscribe((nurse) => {
      this.onNurseSelected(nurse);
    });

    this.leaveForm.valueChanges.subscribe(() => this.refreshPreview());
  }

  private dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fromDateCtrl = group.get('fromDate');
      const toDateCtrl = group.get('toDate');

      if (!fromDateCtrl || !toDateCtrl) return null;

      const fromDate = fromDateCtrl.value as TuiDay;
      const toDate = toDateCtrl.value as TuiDay;

      if (fromDate && toDate) {
        // Check if 'toDate' is strictly before 'fromDate'
        if (toDate.dayBefore(fromDate)) {
          toDateCtrl.setErrors({ ...toDateCtrl.errors, invalidRange: true });
          return { invalidRange: true };
        } else {
          // Clear the error if dates become valid
          if (toDateCtrl.hasError('invalidRange')) {
            const errors = { ...toDateCtrl.errors };
            delete errors['invalidRange'];
            toDateCtrl.setErrors(Object.keys(errors).length ? errors : null);
          }
        }
      }
      return null;
    };
  }

  navigate(link?: string): void {
    if (link) this.router.navigate([link]);
  }

  loadNurses(): void {
    this.nurseService.getAllNurses().subscribe({
      next: (res: any) => {
        this.nurses = Array.isArray(res) ? res : res?.data || [];
      },
      error: () => (this.nurses = []),
    });
  }

  onNurseSelected(nurse: any): void {
    if (!nurse) {
      this.balance = null;
      this.leaveTypeOptions = [];
      this.employeeNumberDisplay = "—";
      this.joinDateDisplay = "—";
      return;
    }
    this.employeeNumberDisplay = getEmployeeNumber(nurse);
    const joinRaw = nurse.appointmentDate || nurse.registrationDate;
    this.joinDateDisplay = joinRaw
      ? new Date(joinRaw).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "—";

    console.log("Nurse Empl Type ==== " + nurse.employmentType)

    if (nurse.employmentType === EMPLOYMENT_TYPE.PERMANENT) {
      this.leaveTypeOptions = [
        { value: LEAVE_TYPE.DAY_OFF, label: "Day-Offs" },
        { value: LEAVE_TYPE.ANNUAL_LEAVE, label: "Annual Leave" },
      ];
    } else {
      this.leaveTypeOptions = [
        { value: LEAVE_TYPE.DAY_OFF, label: "Day-Offs" },
      ];
      const current = this.leaveForm.get("leaveType")?.value;
      if (current?.value === LEAVE_TYPE.ANNUAL_LEAVE) {
        this.leaveForm.patchValue({ leaveType: this.leaveTypeOptions[0] });
      }
    }

    this.leaveService.getBalance(nurse._id).subscribe({
      next: (res: any) => (this.balance = res?.data),
      error: () => (this.balance = null),
    });
  }

  private tuiDayToYmd(day: TuiDay): string {
    const y = day.year;
    const m = String(day.month + 1).padStart(2, "0");
    const d = String(day.day).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  /** Parse API date as a calendar day in Asia/Colombo (avoids UTC off-by-one). */
  private toTuiDay(value: string | Date): TuiDay {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      const [y, m, d] = value.trim().split("-").map(Number);
      return new TuiDay(y, m - 1, d);
    }
    const dt = new Date(value);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Colombo",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(dt);
    const y = Number(parts.find((p) => p.type === "year")?.value);
    const m = Number(parts.find((p) => p.type === "month")?.value);
    const d = Number(parts.find((p) => p.type === "day")?.value);
    return new TuiDay(y, m - 1, d);
  }

  private formatExpandedDate(value: string | Date): string {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const [y, m, d] = value.substring(0, 10).split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }
    const dt = new Date(value);
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  refreshPreview(): void {
    if (this.leaveForm.hasError('invalidRange')) {
      this.preview = null;
      this.expandedDates = [];
      return;
    }
    const nurse = this.leaveForm.get("nurse")?.value;
    const leaveType = this.leaveForm.get("leaveType")?.value;
    const fromDate = this.leaveForm.get("fromDate")?.value;
    const toDate = this.leaveForm.get("toDate")?.value;
    if (!nurse?._id || !leaveType || !fromDate) {
      this.preview = null;
      this.expandedDates = [];
      return;
    }

    const payload = {
      nurseId: nurse._id,
      leaveType: leaveType.value || leaveType,
      // Calendar date only — never toISOString() (shifts the day on UTC servers)
      fromDate: this.tuiDayToYmd(fromDate as TuiDay),
      toDate: toDate ? this.tuiDayToYmd(toDate as TuiDay) : undefined,
      excludeLeaveId: this.leaveId || undefined,
    };

    this.leaveService.preview(payload).subscribe({
      next: (res: any) => {
        this.preview = res?.data;
        this.expandedDates = (res?.data?.dates || []).map((d: string | Date) =>
          this.formatExpandedDate(d)
        );
        if (res?.data?.balance) this.balance = res.data.balance;
      },
      error: () => {
        this.preview = null;
      },
    });
  }

  loadLeave(id: string): void {
    this.isLoading = true;
    this.leaveService.getLeaveById(id).subscribe({
      next: (res: any) => {
        const leave = res?.data;
        if (!leave) {
          this.isLoading = false;
          return;
        }
        const nurse = leave.nurseId;
        this.onNurseSelected(nurse);
        const leaveTypeOpt = this.leaveTypeOptions.find(
          (o) => o.value === leave.leaveType
        ) || {
          value: leave.leaveType,
          label:
            leave.leaveType === LEAVE_TYPE.ANNUAL_LEAVE
              ? "Annual Leave"
              : "Day-Offs",
        };
        this.leaveForm.patchValue({
          nurse,
          leaveType: leaveTypeOpt,
          submittedDate: this.toTuiDay(leave.submittedDate),
          fromDate: this.toTuiDay(leave.fromDate),
          toDate: this.toTuiDay(leave.toDate),
          reason: leave.reason || "",
        });
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  // buildPayload(shiftConflictAction?: string): any {
  //   const raw = this.leaveForm.getRawValue();
  //   const nurse = raw.nurse;
  //   const leaveType = raw.leaveType?.value || raw.leaveType;
  //   return {
  //     nurseId: nurse._id,
  //     leaveType,
  //     submittedDate: raw.submittedDate
  //       ? (raw.submittedDate as TuiDay).toLocalNativeDate().toISOString()
  //       : new Date().toISOString(),
  //     fromDate: (raw.fromDate as TuiDay).toLocalNativeDate().toISOString(),
  //     toDate: raw.toDate
  //       ? (raw.toDate as TuiDay).toLocalNativeDate().toISOString()
  //       : undefined,
  //     reason: raw.reason || "",
  //     shiftConflictAction,
  //   };
  // }

  buildPayload(shiftConflictAction?: string): any {
  const raw = this.leaveForm.getRawValue();
  const nurse = raw.nurse;
  const leaveType = raw.leaveType?.value || raw.leaveType;

  // Helper to format TuiDay or fallback to YYYY-MM-DD
  const formatTuiDay = (day: TuiDay | null | undefined): string | undefined => {
    if (!day) return undefined;
    // Formats directly to "YYYY-MM-DD" without timezone conversion
    return day.toString('YMD', '-');
  };

  return {
    nurseId: nurse._id,
    leaveType,
    submittedDate: raw.submittedDate 
      ? formatTuiDay(raw.submittedDate as TuiDay) 
      : new Date().toISOString().split('T')[0], // "2026-08-17"
    fromDate: formatTuiDay(raw.fromDate as TuiDay),
    toDate: formatTuiDay(raw.toDate as TuiDay),
    reason: raw.reason || "",
    shiftConflictAction,
  };
}

  saveLeave(shiftConflictAction?: string): void {
    if (this.leaveForm.invalid) {
      this.leaveForm.markAllAsTouched();

      if (this.leaveForm.hasError('invalidRange')) {
        this.alerts.open("End Date cannot be earlier than Start Date.", {
          label: "Warning",
          appearance: "warning",
        }).subscribe();
        return;
      }

      this.alerts
        .open("Nurse Name, Leave Type, and From Date are required.", {
          label: "Warning",
          appearance: "warning",
        })
        .subscribe();
      return;
    }

    if (this.preview && this.preview.valid === false) {
      this.alerts
        .open(this.preview.message || "Insufficient leave balance.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
      return;
    }

    this.isLoading = true;
    const payload = this.buildPayload(shiftConflictAction);
    const req$ = this.leaveId
      ? this.leaveService.updateLeave(this.leaveId, payload)
      : this.leaveService.createLeave(payload);

    req$
      .pipe(
        catchError((err) => {
          this.isLoading = false;
          if (err?.status === 409 || err?.error?.requiresAction) {
            this.pendingConflicts = err?.error?.conflicts || [];
            this.showConflictDialog = true;
            return of(null);
          }
          this.alerts
            .open(err?.error?.message || "Failed to save leave", {
              label: "Error",
              appearance: "negative",
            })
            .subscribe();
          return of(null);
        })
      )
      .subscribe((res: any) => {
        this.isLoading = false;
        if (!res) return;
        this.showConflictDialog = false;
        this.alerts
          .open(this.leaveId ? "Leave updated" : "Leave created", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.router.navigateByUrl("/admin/leave");
      });
  }

  keepShift(): void {
    this.showConflictDialog = false;
    this.alerts
      .open(
        "Leave was not applied for conflicting dates. Existing shifts kept.",
        { label: "Info", appearance: "info" }
      )
      .subscribe();
  }

  removeShiftAndSave(): void {
    this.showConflictDialog = false;
    this.saveLeave("RemoveShift");
  }
}
